// API configuration
import { Capacitor } from '@capacitor/core';

// Helper to detect if running on iOS simulator or development mode
const isDevelopmentMode = () => {
  // Check if explicitly set via env var
  if (process.env.REACT_APP_API_URL) {
    console.log('[Config] Using explicit REACT_APP_API_URL');
    return false; // Use the explicit URL
  }
  
  // Check if running in web browser on localhost
  if (!Capacitor.isNativePlatform() && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('[Config] Web browser on localhost');
    return true;
  }
  
  console.log('[Config] Not development mode');
  return false;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  isDevelopmentMode()
    ? 'http://localhost:5001'
    : 'https://wanderingsock.com'
);

// Help content base URL - always fetch from production server
// This ensures iOS apps get the latest Help content without needing a rebuild
export const HELP_BASE_URL = 'https://wanderingsock.com';

export const API_ENDPOINTS = {
  ANALYZE: `${API_BASE_URL}/api/analyze`,
  SAVE_ITEMS: `${API_BASE_URL}/api/save-items`,
  UPDATE_ITEMS: `${API_BASE_URL}/api/update-items`,
  GET_ITEMS: `${API_BASE_URL}/api/items`,
  DELETE_ITEM: (id: number, scope: 'one' | 'image' = 'one') => `${API_BASE_URL}/api/items/${id}?scope=${scope}`,
  GET_IMAGE: (id: number) => `${API_BASE_URL}/api/images/${id}`,
  GET_IMAGE_CONTENT: (id: number) => `${API_BASE_URL}/api/images/${id}/content`,
  DELETE_IMAGE: (id: number) => `${API_BASE_URL}/api/images/${id}`,
  CREATE_IMAGE_FROM_URL: `${API_BASE_URL}/api/images`,
  
  // Queue management endpoints
  QUEUE_ADD: `${API_BASE_URL}/api/queue/add`,
  QUEUE_PENDING: `${API_BASE_URL}/api/queue/pending`,
  QUEUE_UPDATE_STATUS: (id: number) => `${API_BASE_URL}/api/queue/${id}/status`,
  QUEUE_DELETE: (id: number) => `${API_BASE_URL}/api/queue/${id}`,
  QUEUE_BATCH: (batchId: string) => `${API_BASE_URL}/api/queue/batch/${batchId}`,
  QUEUE_REPROCESS: (imageId: number) => `${API_BASE_URL}/api/queue/reprocess/${imageId}`,
  QUEUE_CLEAR_COMPLETED: `${API_BASE_URL}/api/queue/clear-completed`,
  
  // Trash management endpoints
  GET_TRASH: (filter: 'all' | 'today' | 'yesterday' | 'last_delete' = 'all') => `${API_BASE_URL}/api/trash?filter=${filter}`,
  RESTORE_TRASH: `${API_BASE_URL}/api/trash/restore`,
  EMPTY_TRASH: (emptyAll: boolean = false) => `${API_BASE_URL}/api/trash?all=${emptyAll}`,
  
  // App settings endpoints
  GET_SETTING: (key: string) => `${API_BASE_URL}/api/settings/${key}`,
  SET_SETTING: (key: string) => `${API_BASE_URL}/api/settings/${key}`,
  
  // User stats endpoints
  GET_USER_STATS: `${API_BASE_URL}/api/user/stats`,
  GET_ALL_USERS_STATS: `${API_BASE_URL}/api/admin/users/stats`,
  
  // Collection endpoints
  GET_COLLECTIONS: (includeCounts: boolean = false) => `${API_BASE_URL}/api/collections${includeCounts ? '?include_counts=true' : ''}`,
  GET_COLLECTION: (id: number) => `${API_BASE_URL}/api/collections/${id}`,
  CREATE_COLLECTION: `${API_BASE_URL}/api/collections`,
  UPDATE_COLLECTION: (id: number) => `${API_BASE_URL}/api/collections/${id}`,
  DELETE_COLLECTION: (id: number) => `${API_BASE_URL}/api/collections/${id}`,
  GENERATE_COLLECTION_INSURANCE_PDF: (id: number) => `${API_BASE_URL}/api/collections/${id}/insurance-pdf`,
  GET_COLLECTION_ITEMS: (id: number) => `${API_BASE_URL}/api/collections/${id}/items`,
  ADD_ITEMS_TO_COLLECTION: (id: number) => `${API_BASE_URL}/api/collections/${id}/items`,
  REMOVE_ITEM_FROM_COLLECTION: (collectionId: number, itemId: number) => `${API_BASE_URL}/api/collections/${collectionId}/items/${itemId}`,
  GET_COLLECTION_MEMBERSHIP: (itemIds: number[]) => `${API_BASE_URL}/api/collections/membership?item_ids=${itemIds.join(',')}`,
  BATCH_UPDATE_COLLECTIONS: `${API_BASE_URL}/api/collections/batch-update`,
  
  // Category endpoints
  GET_CATEGORIES: (parent?: string, search?: string) => {
    const params = new URLSearchParams();
    if (parent) params.append('parent', parent);
    if (search) params.append('search', search);
    const queryString = params.toString();
    return `${API_BASE_URL}/api/categories${queryString ? `?${queryString}` : ''}`;
  },
  GET_PARENT_CATEGORIES: `${API_BASE_URL}/api/categories/parents`,
  GET_ITEM_CATEGORIES: (itemId: number) => `${API_BASE_URL}/api/items/${itemId}/categories`,
  UPDATE_ITEM_CATEGORIES: (itemId: number) => `${API_BASE_URL}/api/items/${itemId}/categories`,
  
  // Receipt endpoints
  SCAN_RECEIPT: `${API_BASE_URL}/api/receipts/scan`,
  ADD_RECEIPT_TO_COLLECTION: `${API_BASE_URL}/api/receipts/add-to-collection`,
  
  // Bindle (saved search) endpoints
  GET_BINDLES: (collectionId?: number) => `${API_BASE_URL}/api/bindles${collectionId != null ? `?collection_id=${collectionId}` : ''}`,
  CREATE_BINDLE: `${API_BASE_URL}/api/bindles`,
  UPDATE_BINDLE: (id: number) => `${API_BASE_URL}/api/bindles/${id}`,
  DELETE_BINDLE: (id: number) => `${API_BASE_URL}/api/bindles/${id}`,

  // Recipe endpoints (Kitchen only)
  GET_RECIPES: `${API_BASE_URL}/api/recipes`,
  GET_RECIPE: (id: number) => `${API_BASE_URL}/api/recipes/${id}`,
  CREATE_RECIPE: `${API_BASE_URL}/api/recipes`,
  UPDATE_RECIPE: (id: number) => `${API_BASE_URL}/api/recipes/${id}`,
  DELETE_RECIPE: (id: number) => `${API_BASE_URL}/api/recipes/${id}`,
  SHARE_RECIPE: (id: number) => `${API_BASE_URL}/api/recipes/${id}/share`,
  SEARCH_RECIPES_ONLINE: `${API_BASE_URL}/api/recipes/search`,
  GET_ONLINE_RECIPE_DETAILS: (id: number) => `${API_BASE_URL}/api/recipes/search/${id}/details`,

  // Recipe Library (shared, Spoonacular-sourced, user_id=-1)
  GET_LIBRARY_RECIPES: `${API_BASE_URL}/api/library/recipes`,
  SAVE_LIBRARY_RECIPE: `${API_BASE_URL}/api/library/recipes/save`,
  CHECKOUT_LIBRARY_RECIPE: (id: number) => `${API_BASE_URL}/api/library/recipes/${id}/checkout`,
  FORK_LIBRARY_RECIPE: (id: number) => `${API_BASE_URL}/api/library/recipes/${id}/fork`,

  // Spaces (Hierarchical Organization)
  GET_SPACES: `${API_BASE_URL}/api/spaces`,
  CREATE_SPACE: `${API_BASE_URL}/api/spaces`,
  UPDATE_SPACE: (id: number) => `${API_BASE_URL}/api/spaces/${id}`,
  DELETE_SPACE: (id: number) => `${API_BASE_URL}/api/spaces/${id}`,
};

export default API_BASE_URL;
